import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ExitToAppIcon from '@mui/icons-material/ExitToApp'; // Replaces LogoutIcon
import ClassIcon from '@mui/icons-material/Class'; // For Cursus
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'; // For Courses
import LocalOfferIcon from '@mui/icons-material/LocalOffer'; // For Marketing
import SettingsIcon from '@mui/icons-material/Settings'; // For Settings
import FaceIcon from '@mui/icons-material/Face';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useAuth } from '../../context/AuthContext';
import HelpIcon from '@mui/icons-material/Help';
import TutorialMenu from '../common/TutorialMenu';
import { TourType } from '../../config/TourConfigurations';

/**
 * Props for the Taskbar component
 */
interface TaskbarProps {
    /** Optional callback to start a guided tour */
    onStartTour?: (tourType: TourType) => void;
    /** Contrast mode based on background brightness */
    contrastMode?: 'light' | 'dark';
}

/**
 * Taskbar component that appears at the bottom of the admin desktop interface.
 * Provides quick access to all applications, help menu, and logout functionality.
 *
 * @param props - The props for the component
 * @returns The Taskbar UI
 */
const Taskbar: React.FC<TaskbarProps> = ({ onStartTour, contrastMode = 'dark' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, user } = useAuth();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [version, setVersion] = useState<string>('');

    // Fetch current version
    React.useEffect(() => {
        const fetchVersion = async () => {
            try {
                const res = await fetch('/api/system/version');
                const data = await res.json();
                setVersion(data.version || '');
            } catch (err) {
                console.error('Error fetching version', err);
            }
        };
        fetchVersion();
    }, []);

    const isLightContrast = contrastMode === 'light';

    // Detect mobile
    const theme = React.useMemo(() => ({ breakpoints: { down: (key: string) => `(max-width:${key === 'sm' ? '600px' : '900px'})` } }), []);
    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 600);

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Thematic colors
    const taskbarBg = isLightContrast ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.2)';
    const itemColor = isLightContrast ? 'rgba(0, 0, 0, 0.7)' : 'white';
    const hoverBg = isLightContrast ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.3)';
    const activeBg = isLightContrast ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.2)';
    const borderColor = isLightContrast ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.18)';
    const shadowColor = isLightContrast ? 'rgba(0, 0, 0, 0.2)' : 'rgba(31, 38, 135, 0.37)';

    // Mobile: show only essential apps (dashboard, settings + logout is always shown)
    const mobileEssentialIds = ['dashboard', 'settings'];

    const allApps = [
        { id: 'dashboard', name: 'Tableau de bord', icon: <DashboardIcon />, path: '/dashboard', alwaysShow: true },
        { id: 'cursus', name: 'Parcours', icon: <ClassIcon />, path: '/parcours' },
        { id: 'courses', name: 'Cours', icon: <LibraryBooksIcon />, path: '/courses' },
        { id: 'marketing', name: 'Marketing', icon: <LocalOfferIcon />, path: '/marketing' },
        { id: 'users', name: 'Utilisateurs', icon: <PeopleIcon />, path: '/users' },
        { id: 'apprenants', name: 'Apprenants', icon: <FaceIcon />, path: '/learners' },
        { id: 'monof', name: 'Mon OF', icon: <BusinessIcon />, path: '/mon-of' },
        { id: 'block-evaluation', name: 'Évaluation de Bloc', icon: <AssignmentIcon />, path: '/block-evaluation' },
        { id: 'settings', name: 'Paramètres', icon: <SettingsIcon />, path: '/settings', alwaysShow: true },
    ];

    const apps = allApps.filter(app => {
        // On mobile, only show essential apps
        if (isMobile) {
            return mobileEssentialIds.includes(app.id);
        }
        if (app.alwaysShow) return true;
        // Check both authorization and enablement
        // @ts-ignore - Dynamic access to authorizedApps
        const isAuthorized = user?.authorizedApps?.[app.id] ?? false;
        // @ts-ignore - Dynamic access to enabledApps
        const isEnabled = user?.preferences?.enabledApps?.[app.id] ?? true;
        return isAuthorized && isEnabled;
    });

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
    };

    const handleSelectTour = (tourType: TourType) => {
        if (onStartTour) {
            onStartTour(tourType);
        }
    };

    return (
        <Box
            id="taskbar"
            sx={{
                position: 'fixed',
                bottom: { xs: 12, sm: 16, md: 20 },
                left: '50%',
                transform: 'translateX(-50%)',
                height: { xs: 52, sm: 58, md: 64 },
                width: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)', md: 'auto' },
                maxWidth: { xs: '100vw', sm: '90vw', md: 'auto' },
                bgcolor: taskbarBg,
                backdropFilter: 'blur(16px)',
                borderRadius: { xs: 3, sm: 4 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'center' },
                px: { xs: 1, sm: 1.5, md: 2 },
                gap: { xs: 0.5, sm: 0.75, md: 1 },
                boxShadow: `0 8px 32px 0 ${shadowColor}`,
                border: `1px solid ${borderColor}`,
                zIndex: 1300,
                overflowX: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
            }}
        >
            {apps.map((app) => {
                const isActive = location.pathname.startsWith(app.path);
                return (
                    <Tooltip key={app.name} title={app.name} arrow placement="top">
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={() => navigate(app.path)}
                                sx={{
                                    color: itemColor,
                                    bgcolor: isActive ? activeBg : 'transparent',
                                    borderRadius: { xs: 2, md: 3 },
                                    p: { xs: 1, sm: 1.25, md: 1.5 },
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: hoverBg,
                                        transform: 'translateY(-4px)',
                                    },
                                    '& svg': {
                                        fontSize: { xs: 22, sm: 25, md: 28 },
                                    }
                                }}
                            >
                                {app.icon}
                            </IconButton>
                            {isActive && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: -8,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 4,
                                        height: 4,
                                        borderRadius: '50%',
                                        bgcolor: itemColor,
                                    }}
                                />
                            )}
                        </Box>
                    </Tooltip>
                );
            })}

            <Box sx={{ width: 1, height: 32, bgcolor: borderColor, mx: 1 }} />

            {onStartTour && (
                <>
                    <Tooltip title="Aide & Visite guidée" arrow placement="top">
                        <IconButton
                            onClick={handleOpenMenu}
                            sx={{
                                color: isLightContrast ? '#1976d2' : '#e3f2fd',
                                borderRadius: 3,
                                p: 1.5,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    bgcolor: isLightContrast ? 'rgba(25, 118, 210, 0.1)' : 'rgba(33, 150, 243, 0.2)',
                                    transform: 'translateY(-4px)',
                                },
                                '& svg': {
                                    fontSize: 28,
                                }
                            }}
                        >
                            <HelpIcon />
                        </IconButton>
                    </Tooltip>
                    <TutorialMenu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={handleCloseMenu}
                        onSelectTour={handleSelectTour}
                    // Optionally pass contrastMode to TutorialMenu if needed
                    />
                </>
            )}

            {/* Version Indicator */}
            {version && (
                <Tooltip title="Notes de version" arrow placement="top">
                    <Box
                        onClick={() => navigate('/patchnotes')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: isLightContrast ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.3)',
                            color: isLightContrast ? '#667eea' : '#e0e7ff',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: isLightContrast ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.5)',
                                transform: 'translateY(-2px)',
                            }
                        }}
                    >
                        v{version}
                    </Box>
                </Tooltip>
            )}

            <Tooltip title="Déconnexion" arrow placement="top">
                <IconButton
                    onClick={handleLogout}
                    sx={{
                        color: isLightContrast ? '#d32f2f' : '#ffcdd2',
                        borderRadius: 3,
                        p: 1.5,
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: isLightContrast ? 'rgba(211, 47, 47, 0.1)' : 'rgba(255, 0, 0, 0.2)',
                            transform: 'translateY(-4px)',
                        },
                        '& svg': {
                            fontSize: 28,
                        }
                    }}
                >
                    <ExitToAppIcon />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

export default Taskbar;
